import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight } from 'lucide-react'
import ToolCard from '../components/ToolCard'
import { apps } from '../config/apps.config'

export default function LandingPage() {
  return (
    <div className="flex flex-col">
      <Helmet>
        <title>Headstate Studio - Free Online Creative Suite | DAW, Video, Image & 3D Editor</title>
        <meta name="description" content="Headstate Studio is a free, browser-based creative suite featuring a digital audio workstation (DAW), video editor, image editor, and 3D editor." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-40 pb-32 lg:pt-56 lg:pb-48 relative overflow-hidden">
        {/* Deep Glossy Background Patterns */}
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/[0.03] via-hs-bg to-hs-bg pointer-events-none" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full shadow-neu-in-deep bg-black/40 border border-white/5 mb-8">
             <span className="w-2 h-2 rounded-full bg-hs-core shadow-[0_0_8px_#4F98FF] mr-3 animate-pulse"></span>
             <span className="text-[10px] font-mono font-bold text-white/70 uppercase tracking-widest">Studio V2 Now Live</span>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold mb-8 tracking-tighter text-gloss pb-2">
            CREATE WITHOUT LIMITS.
          </h1>
          <p className="text-xl md:text-2xl text-hs-text font-light max-w-3xl mx-auto mb-12 leading-relaxed drop-shadow-md">
            One unified studio for all your creative needs. Audio, video, image, and 3D editing tools - all in one seamless environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/dashboard"
              className="relative group neu-button px-8 py-4 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-hs-core to-[#1A5CB8] opacity-80 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.4)_0%,rgba(255,255,255,0)_50%)] pointer-events-none"></div>
              <span className="relative z-10 flex items-center justify-center font-mono font-bold text-sm text-white tracking-widest uppercase drop-shadow-md">
                Open Dashboard
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
              </span>
            </Link>
            <a
              href="#tools"
              className="neu-button px-8 py-4 font-mono text-sm tracking-widest uppercase text-hs-text hover:text-white transition-colors flex items-center justify-center"
            >
              Explore Tools
            </a>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="tools" className="py-32 relative">
        <div className="absolute inset-0 bg-hs-charcoal/20 shadow-neu-in-deep pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6 tracking-tight text-gloss">
              YOUR CREATIVE TOOLKIT
            </h2>
            <p className="text-hs-text max-w-xl mx-auto font-light text-lg">
              Professional-grade tools, completely reimagined with a premium feel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {apps.map((tool) => (
              <ToolCard key={tool.id} {...tool} />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center p-10 neu-panel group relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none z-0"></div>
              <div className="relative z-10 w-20 h-20 neu-panel-inset rounded-full flex items-center justify-center mx-auto mb-8 border border-black/50">
                <span className="text-3xl drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">⚡</span>
              </div>
              <h3 className="relative z-10 text-xl font-display font-bold text-white mb-4 tracking-tight">UNIFIED WORKSPACE</h3>
              <p className="relative z-10 text-hs-text font-light leading-relaxed">
                Switch between tools seamlessly without losing your workflow context in our optimized environment.
              </p>
            </div>
            
            <div className="text-center p-10 neu-panel group relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none z-0"></div>
              <div className="relative z-10 w-20 h-20 neu-panel-inset rounded-full flex items-center justify-center mx-auto mb-8 border border-black/50">
                <span className="text-3xl drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">☁️</span>
              </div>
              <h3 className="relative z-10 text-xl font-display font-bold text-white mb-4 tracking-tight">CLOUD READY</h3>
              <p className="relative z-10 text-hs-text font-light leading-relaxed">
                Access your projects from anywhere with blazing-fast cloud storage integration.
              </p>
            </div>

            <div className="text-center p-10 neu-panel group relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none z-0"></div>
              <div className="relative z-10 w-20 h-20 neu-panel-inset rounded-full flex items-center justify-center mx-auto mb-8 border border-black/50">
                <span className="text-3xl drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">🤝</span>
              </div>
              <h3 className="relative z-10 text-xl font-display font-bold text-white mb-4 tracking-tight">OPEN SOURCE</h3>
              <p className="relative z-10 text-hs-text font-light leading-relaxed">
                Built with powerful open-source tools, crafted meticulously by creators, for creators.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
